import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare, Send, Trash2, Download, Loader2,
  Paperclip, Video, CornerUpLeft, X, Filter, Play,
  Smile, Users, Bell, ArrowLeft, Menu, MoreVertical
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { THEME } from '../theme';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

// --- CUSTOM HOOK (LOGIC UNTOUCHED) ---
const useTaskChat = (taskId, meetingId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchMessages = async () => {
    if (!taskId || !meetingId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/chat?meetingId=${meetingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) { console.error("Poll error", err); } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [taskId, meetingId]);

  const sendMessage = async (text, file = null, replyToId = null, replyToContent = null, replyToSender = null) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/tasks/${taskId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text, meetingId, file, replyToId, replyToContent, replyToSender })
      });
      fetchMessages();
    } catch (err) { console.error("Send error", err); }
  };

  const deleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/tasks/${taskId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messageId, meetingId })
      });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_deleted: true, content: "🚫 This message was deleted" } : m));
    } catch (err) { console.error("Delete error", err); }
  };

  return { messages, loading, sendMessage, deleteMessage };
};

// --- MAIN COMPONENT ---
const TasksPage = ({ darkMode, user }) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [inputText, setInputText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // New States for Features
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [teamRequestsCount, setTeamRequestsCount] = useState(2);

  // UI Responsive States
  const [showMobileChat, setShowMobileChat] = useState(false);

  const { messages, sendMessage, deleteMessage } = useTaskChat(selectedTask?.task_id, selectedTask?.meeting_id);

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  // 1. Fetch Tasks
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/tasks`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) {
          setTasks(data.data || []);
          // On Desktop only: auto-select first task
          if (window.innerWidth > 768 && data.data?.length > 0 && !selectedTask) {
            // Optional: setSelectedTask(data.data[0]); 
          }
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchTasks();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showMobileChat]); // Added showMobileChat dependency for scroll on open

  // 2. Mark as Read Logic
  const markAsRead = async (task) => {
    if (!task || task.unseen_count === 0) return;

    setTasks(prev => prev.map(t => t.task_id === task.task_id ? { ...t, unseen_count: 0 } : t));

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/tasks/${task.task_id}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error("Read sync error", e); }
  };

  const handleSelectTask = (t) => {
    setSelectedTask(t);
    markAsRead(t);
    setShowMobileChat(false); // Reset mobile chat view when switching tasks
  };

  const handleBackToList = () => {
    setSelectedTask(null);
    setShowMobileChat(false);
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    sendMessage(inputText, null, replyTo?.id, replyTo?.content, replyTo?.sender_name);
    setInputText("");
    setReplyTo(null);
    setShowEmojiPicker(false);
  };

  const onEmojiClick = (emojiObject) => {
    setInputText(prev => prev + emojiObject.emoji);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    setTimeout(() => {
      sendMessage(`Shared a file: ${file.name}`, { name: file.name, url: "#", type: file.type });
      setIsUploading(false);
    }, 1000);
  };

  const totalUnseenMessages = tasks.reduce((acc, t) => acc + (t.unseen_count || 0), 0);

  if (loading) return <div className="h-screen w-screen flex items-center justify-center bg-primary"><Loader2 className="animate-spin text-secondary" /></div>;
  // --- 1. Function to control the video player ---
  const handleJumpToTime = (timeString) => {
    if (!videoRef.current) return;

    // Convert "MM:SS" or "HH:MM:SS" to seconds
    const parts = timeString.split(':').map(Number);
    let seconds = 0;

    if (parts.length === 2) {
      // MM:SS
      seconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
      // HH:MM:SS
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    videoRef.current.currentTime = seconds;
    videoRef.current.play();
  };

  // --- 2. Function to parse text and render clickable timestamps ---
  const renderMessageContent = (text, isMe) => {
    if (!text) return null;

    // Regex to find time patterns like 0:23, 12:30, 1:05:20
    // \b ensures we don't match things like IP addresses arbitrarily, though simple context works best here
    const regex = /(\b\d{1,2}:\d{2}(?::\d{2})?\b)/g;

    // Split the text by the regex. The capturing group () keeps the timestamp in the array.
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // If the part matches the time regex, render it as a button
      if (part.match(regex)) {
        return (
          <button
            key={index}
            onClick={(e) => { e.stopPropagation(); handleJumpToTime(part); }}
            className={`font-bold px-1 rounded mx-0.5 inline-flex items-center hover:underline ${isMe
                ? 'text-white bg-white/20 hover:bg-white/30'
                : 'text-blue-500 bg-blue-500/10 hover:bg-blue-500/20'
              }`}
            title="Jump to video time"
          >
            <Play size={10} className="mr-1 fill-current" />
            {part}
          </button>
        );
      }
      // Otherwise render plain text
      return <span key={index}>{part}</span>;
    });
  };
  return (
    <div className={`flex h-screen w-full overflow-hidden relative font-light ${darkMode ? 'bg-primary text-white' : 'bg-slate-50 text-slate-900'}`}>

      {/* --------------------------------------------------------------------------- */}
      {/* LEFT SIDEBAR (Inbox)                                                       */}
      {/* Logic: Hidden on mobile if a task is selected. Visible on Desktop always.  */}
      {/* --------------------------------------------------------------------------- */}
      <div className={`
        flex-col border-r flex-shrink-0 h-full transition-all duration-300
        ${darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white'}
        ${selectedTask ? 'hidden md:flex w-full md:w-80' : 'flex w-full md:w-80'}
      `}>

        {/* DASHBOARD HEADER */}
        <div className={`p-5 border-b flex flex-col gap-4 shrink-0 ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex justify-between items-center">
            <h1 className="font-bold text-lg tracking-tight">Inbox</h1>
            <Filter size={16} className="opacity-40 hover:opacity-100 cursor-pointer" />
          </div>

          <div className="flex gap-2">
            <div className={`flex-1 p-2 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <p className="text-[10px] opacity-50 uppercase font-bold mb-1">Unseen</p>
              <div className="flex items-center gap-2">
                <Bell size={16} className={totalUnseenMessages > 0 ? "text-red-500" : "opacity-30"} />
                <span className="text-xl font-bold">{totalUnseenMessages}</span>
              </div>
            </div>
            <div className={`flex-1 p-2 rounded-lg border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <p className="text-[10px] opacity-50 uppercase font-bold mb-1">Requests</p>
              <div className="flex items-center gap-2">
                <Users size={16} className="text-secondary" />
                <span className="text-xl font-bold">{teamRequestsCount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* TASK LIST */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {tasks.map(t => (
            <button
              key={t.task_id}
              onClick={() => handleSelectTask(t)}
              className={`w-full p-4 text-left border-b transition-all relative group 
                ${darkMode ? 'border-slate-800' : 'border-slate-100'}
                ${selectedTask?.task_id === t.task_id
                  ? 'bg-secondary/10'
                  : (darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50')}
              `}
            >
              {selectedTask?.task_id === t.task_id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-secondary" />}

              <div className="flex justify-between items-start mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-secondary">Task</span>
                {t.unseen_count > 0 ? (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                    {t.unseen_count}
                  </span>
                ) : (
                  <span className="text-[10px] opacity-40">{new Date(t.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                )}
              </div>

              <p className={`text-sm font-semibold line-clamp-2 leading-snug mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{t.description}</p>

              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {t.assignees?.map((a, i) => (
                    <div key={i} className={`w-5 h-5 rounded-full border flex items-center justify-center text-[8px] font-bold text-white ${darkMode ? 'border-slate-900' : 'border-white'} ${['bg-purple-500', 'bg-pink-500', 'bg-blue-500'][i % 3]}`}>{(a.name || "U")[0]}</div>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>

        {teamRequestsCount > 0 && (
          <div className={`p-3 text-xs font-semibold text-center border-t cursor-pointer hover:bg-secondary/10 transition-colors text-secondary ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            View Requests
          </div>
        )}
      </div>
      <div className={`
        flex-col min-w-0 overflow-hidden h-full relative
        ${selectedTask ? 'flex flex-1' : 'hidden md:flex flex-1'}
      `}>
        {selectedTask ? (
          <>
            <div className={`md:hidden p-3 border-b flex items-center justify-between shrink-0 ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
              <button onClick={handleBackToList} className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
                <ArrowLeft size={20} />
              </button>
              <span className="font-bold text-sm truncate max-w-[150px]">Task Details</span>
              <button
                onClick={() => setShowMobileChat(!showMobileChat)}
                className={`p-2 rounded-full relative ${showMobileChat ? 'bg-secondary text-white' : (darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100')}`}
              >
                <MessageSquare size={20} />
                {messages.length > 0 && !showMobileChat && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
              </button>
            </div>
            <div className={`flex-1 flex flex-col overflow-y-auto custom-scrollbar ${darkMode ? 'bg-primary' : 'bg-white'}`}>
              <div className={`w-full aspect-video shrink-0 relative sticky top-0 z-10 shadow-lg ${darkMode ? 'bg-black' : 'bg-gray-100'}`}>
                {selectedTask.video_url ? (
                  <video ref={videoRef} src={selectedTask.video_url} controls className="w-full h-full object-contain" />
                ) : (
                  <div className={`absolute inset-0 flex flex-col items-center justify-center ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>
                    <Play size={48} className="mb-2 opacity-50" /><p>Video unavailable</p>
                  </div>
                )}
              </div>
              <div className="p-4 md:p-8 w-full max-w-4xl mx-auto">
                <h1 className={`text-xl md:text-2xl font-bold mb-4 leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>{selectedTask.description}</h1>
                <div className={`flex items-center gap-4 md:gap-6 text-sm opacity-70 pb-6 border-b ${darkMode ? 'border-slate-800 text-white' : 'border-slate-200 text-slate-600'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">{selectedTask.creator_info?.name?.[0] || 'A'}</div>
                    <div><p className="font-semibold text-inherit">{selectedTask.creator_info?.name || 'Admin'}</p><p className="text-xs opacity-60">Meeting Host</p></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className={`flex-1 flex flex-col items-center justify-center opacity-30 text-center p-4 ${darkMode ? 'text-white' : 'text-slate-500'}`}>
            <MessageSquare size={64} className="mb-4" />
            <h3 className="text-xl font-bold">Select a task to view details</h3>
            <p className="text-sm mt-2 hidden md:block">Choose from the inbox on the left</p>
          </div>
        )}
      </div>
      {selectedTask && (
        <div className={`
            flex flex-col h-full border-l transition-transform duration-300 ease-in-out
            ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}
            
            /* Responsive Classes */
            md:relative md:w-[350px] lg:w-[400px] md:translate-x-0
            fixed inset-0 z-50 md:z-auto
            ${showMobileChat ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
        `}>
          <div className={`p-4 border-b flex items-center justify-between shrink-0 h-[60px] md:h-auto ${darkMode ? 'border-slate-800 text-white' : 'border-slate-200 text-slate-900'}`}>
            <h3 className="font-bold text-sm flex items-center gap-2">
              <MessageSquare size={16} /> Discussion ({messages.length})
            </h3>
            <button onClick={() => setShowMobileChat(false)} className={`md:hidden p-2 rounded-full ${darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-inherit">
            {messages.length === 0 ? (
              <div className={`h-full flex flex-col items-center justify-center opacity-30 text-center ${darkMode ? 'text-white' : 'text-slate-500'}`}><p className="text-sm">No comments yet.</p></div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_id === (user._id || user.id);
                return (
                  <div key={msg.id} className={`group flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white ${isMe ? 'bg-secondary' : (darkMode ? 'bg-slate-600' : 'bg-slate-400')}`}>{(msg.sender_name || "U")[0]}</div>
                    <div className={`max-w-[85%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold opacity-80 ${darkMode ? 'text-white' : 'text-slate-700'}`}>{msg.sender_name}</span>
                        <span className={`text-[10px] opacity-40 ${darkMode ? 'text-white' : 'text-slate-500'}`}>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {(msg.reply_context || msg.reply_to_content) && (
                        <div className={`flex flex-col mb-1 px-3 py-2 rounded-lg border text-[10px] w-fit max-w-full relative ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-secondary rounded-l-lg"></div>
                          <div className="flex items-center gap-1 opacity-80 mb-0.5">
                            <CornerUpLeft size={10} className="text-secondary" />
                            <span className="font-bold text-secondary">{msg.reply_context?.sender || "Replying"}</span>
                          </div>
                          <span className={`truncate italic max-w-[150px] opacity-70 ${darkMode ? 'text-white' : 'text-slate-600'}`}>{msg.reply_context?.text || msg.reply_to_content}</span>
                        </div>
                      )}

                      <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed break-words ${msg.is_deleted ? 'italic opacity-50 border border-dashed bg-transparent' : isMe ? 'bg-secondary text-white rounded-tr-none' : (darkMode ? 'bg-slate-800 text-slate-200 rounded-tl-none' : 'bg-slate-100 text-slate-800 rounded-tl-none')}`}>
                        {renderMessageContent(msg.content, isMe)}
                        {msg.file && <a href={msg.file.url} download className="block mt-2 flex items-center gap-2 p-2 bg-black/20 rounded-lg text-xs hover:bg-black/30"><Download size={14} /> <span className="truncate max-w-[150px]">{msg.file.name}</span></a>}
                      </div>

                      {!msg.is_deleted && (
                        <div className={`flex items-center gap-3 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${darkMode ? 'text-white' : 'text-slate-500'}`}>
                          <button onClick={() => setReplyTo(msg)} className="text-[10px] hover:text-secondary font-medium">Reply</button>
                          {isMe && <button onClick={() => deleteMessage(msg.id)} className="text-[10px] hover:text-red-500 font-medium">Delete</button>}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
            <div ref={chatEndRef} />
          </div>

          {/* INPUT AREA */}
          <div className={`p-3 md:p-4 border-t shrink-0 pb-safe ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
            {replyTo && (
              <div className={`flex justify-between items-center mb-2 px-3 py-1.5 rounded border-l-2 border-secondary text-xs animate-in slide-in-from-bottom-2 ${darkMode ? 'bg-secondary/10 text-white' : 'bg-blue-50 text-slate-800'}`}>
                <span className="truncate opacity-80 flex items-center gap-2"><CornerUpLeft size={12} /> Replying to: <b>{replyTo.sender_name || "User"}</b></span>
                <button onClick={() => setReplyTo(null)} className="hover:text-secondary"><X size={12} /></button>
              </div>
            )}
            <form onSubmit={handleSend} className={`flex items-end gap-2 p-2 rounded-xl border transition-all ${darkMode ? 'bg-slate-800 border-slate-700 focus-within:border-secondary/50' : 'bg-gray-50 border-gray-200 focus-within:border-secondary/50'}`}>
              <div className="relative hidden md:block"> {/* Emoji hidden on tiny screens to save space */}
                {showEmojiPicker && (
                  <div className="absolute bottom-12 left-0 z-50 shadow-xl">
                    <EmojiPicker onEmojiClick={onEmojiClick} theme={darkMode ? "dark" : "light"} width={300} height={400} />
                  </div>
                )}
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 opacity-40 hover:opacity-100 rounded-lg transition-all ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-200'}`}><Smile size={18} /></button>
              </div>

              <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
              <button type="button" onClick={() => fileInputRef.current.click()} className={`p-2 opacity-40 hover:opacity-100 rounded-lg ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-200'}`}>{isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}</button>

              <textarea
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                placeholder="Message..."
                className={`flex-1 bg-transparent border-none outline-none text-sm py-2 px-1 max-h-24 resize-none custom-scrollbar placeholder:opacity-40 ${darkMode ? 'text-white' : 'text-slate-900'}`}
                rows={1}
              />
              <button type="submit" disabled={!inputText.trim()} className="p-2 bg-secondary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:bg-transparent disabled:text-gray-500"><Send size={16} /></button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;